import React, { ReactNode } from 'react';
import { getDefaultChainColor, getDefaultPocketColor } from '../../contexts/ControlTypes';
import { render_chain_ids } from '../../helpers/TableHelper';
import {
   defaultPocketRepresentation,
   defaultRestRepresentation,
   Representation,
   StructureComponentType,
} from '../molstar/Controls/StructureRepresentationComponent';
import { AhojTableBase } from './AhojTableBase';
import ColorPairButton from './ColorPairButton';
import StructureRepresentationControlComponent from './StructureRepresentationControlComponent';

/**
 * Table showing structures where all binding-site residues are **unobserved**
 * (absent from the electron density / coordinates) but the sequence is present.
 * These are loaded into Molstar so the chain is visible, but pocket-residue
 * focus is not available since those residues have no 3D coordinates.
 */
class UnobservedSitesTable extends AhojTableBase {
   render(): ReactNode {
      const sortedData = this.getSortedData(this.props.data.found_unobserved);
      const exp = this.expandedColumns;
      // DEFAULT: 10 cols — Structure | AT | Lig | pRMSD | SASA | 👁 | ✏ | Seq | ℹ | 🎨
      // EXPANDED: 17 cols — + Res | MBR | Unobs% | Dis | RMSD | TM | Vol
      const colSpan = exp ? 17 : 10;
      return (
         <div className="found-struct-box">
            {this.props.data.num_found_unobserved_chains > 0 && (
               <div className="found-struct-table scrollable-table">
                  <table id="unobserved-results">
                     <thead>
                        <tr>
                           <th data-tooltip="Matching structure (PDB_ID/chains)">Structure</th>
                           <th
                              data-tooltip={`Aligned to chain(s) of query structure (${this.props.data.target_pdb_id}). *query POI on hover.`}
                              className="at"
                           >
                              AT
                           </th>
                           {exp && (
                              <>
                                 <th
                                    data-tooltip="Resolution of the PDB structure (the best resolution is stated in the presence of multiple). Click to sort."
                                    style={this.sortableThStyle}
                                    onClick={(): void => this.handleSort('resolution')}
                                 >
                                    Res{this.sortIndicator('resolution')}
                                 </th>
                                 <th
                                    data-tooltip="Mapped binding residues (observed). Number of mapped binding residues in UniProt and SEQRES that are observed in the structure (#resis3 in results). *MBR-SEQRES on hover. Click to sort."
                                    className="so"
                                    style={this.sortableThStyle}
                                    onClick={(): void => this.handleSort('mapped_binding_residues_observed')}
                                 >
                                    MBR{this.sortIndicator('mapped_binding_residues_observed')}
                                 </th>
                                 <th
                                    data-tooltip="Percentage of binding residues that are unobserved (disordered) in this structure. Click to sort."
                                    style={this.sortableThStyle}
                                    onClick={(): void => this.handleSort('mapped_binding_residues_unobserved')}
                                 >
                                    Unobs%{this.sortIndicator('mapped_binding_residues_unobserved')}
                                 </th>
                                 <th
                                    data-tooltip="Disorder (unobserved residues). Number of mapped binding residues in UniProt and SEQRES that are not observed in the structure. Click to sort."
                                    style={this.sortableThStyle}
                                    onClick={(): void => this.handleSort('mapped_binding_residues_unobserved')}
                                 >
                                    Dis{this.sortIndicator('mapped_binding_residues_unobserved')}
                                 </th>
                                 <th
                                    data-tooltip="RMSD from the structural alignment (MM-align) of relevant chains (Ca atoms). Click to sort."
                                    style={this.sortableThStyle}
                                    onClick={(): void => this.handleSort('rmsd')}
                                 >
                                    RMSD{this.sortIndicator('rmsd')}
                                 </th>
                              </>
                           )}
                           <th data-tooltip="Ligands present in query pocket or mapped candidate pocket.">Lig</th>
                           <th
                              data-tooltip="Pocket RMSD: all-atom RMSD from the sequence-based pocket alignment (0 refinement cycles). Click to sort."
                              style={this.sortableThStyle}
                              onClick={(): void => this.handleSort('pocket_rmsd')}
                           >
                              pRMSD{this.sortIndicator('pocket_rmsd')}
                           </th>
                           <th
                              data-tooltip="Solvent Accessible Surface Area of pocket residues (UniProt SEQRES observed). Click to sort."
                              style={this.sortableThStyle}
                              onClick={(): void => this.handleSort('sasa')}
                           >
                              SASA{this.sortIndicator('sasa')}
                           </th>
                           {exp && (
                              <>
                                 <th
                                    data-tooltip="TM-score 1 from the structural alignment (MM-align) of the relevant candidate chains onto the relevant query chains. Click to sort."
                                    style={this.sortableThStyle}
                                    onClick={(): void => this.handleSort('tm_score')}
                                 >
                                    TM{this.sortIndicator('tm_score')}
                                 </th>
                                 <th data-tooltip="Pocket volume calculated by PyVOL (default settings).">Vol</th>
                              </>
                           )}
                           <th data-tooltip="Show/hide first 20 structures">
                              <button
                                 className="btn-showhideall"
                                 style={{ width: '1em' }}
                                 onClick={(): void => this.showHideAll(sortedData)}
                              >
                                 <img src="/icons/eye.svg" alt="show/hide" width="100%" />
                              </button>
                           </th>
                           <th data-tooltip="Edit representation">&#9998;</th>
                           <th data-tooltip="Add/remove from Sequence Viewer">Seq</th>
                           <th
                              data-tooltip={exp ? 'Collapse to default columns' : 'Expand to show all columns'}
                              style={{ cursor: 'pointer' }}
                              className={exp ? 'active' : ''}
                              onClick={(): void => this.toggleExpandedColumns()}
                           >
                              ℹ
                           </th>
                           <th data-tooltip="Quick color — click a row to apply a unique color pair">
                              {this.hasAnyColorPairActive(sortedData) ? (
                                 <button title="Reset all colors" onClick={(e): void => this.resetAllColors(e)}>
                                    &#8635;
                                 </button>
                              ) : (
                                 <span style={{ opacity: 0.35, fontSize: '0.85em' }}>◉</span>
                              )}
                           </th>
                        </tr>
                     </thead>
                     <tbody>
                        {sortedData.map((unobserved) => (
                           <React.Fragment key={unobserved.row_id}>
                              <tr
                                 data-row-id={unobserved.row_id}
                                 onClick={(): void => this.selectUnselectStructure(unobserved)}
                                 className={`clickable-row ${this.context.controlsMap.get(unobserved.row_id)?.isSelected ? 'selected-row' : ''}`}
                              >
                                 <td>
                                    <a
                                       href={`https://www.rcsb.org/structure/${unobserved.pdb_id}`}
                                       target="_blank"
                                       title="go to rcsb"
                                    >
                                       {unobserved.pdb_id}
                                    </a>
                                    &nbsp;<span className="slash">/</span>&nbsp;{render_chain_ids(unobserved.chains)}
                                 </td>
                                 <td title={`target poi alt: ${unobserved.target_poi_alt}`}>
                                    {render_chain_ids(unobserved.target_chains)}
                                 </td>
                                 {exp && (
                                    <>
                                       <td title={`Exp. method: ${unobserved.experimental_method}`}>
                                          {unobserved.resolution}
                                       </td>
                                       <td
                                          title={`MBR-SEQRES: ${unobserved.mapped_binding_residues_num ?? 0} (${unobserved.mapped_binding_residues_percent ?? 0}%)`}
                                       >
                                          {unobserved.mapped_binding_residues_observed}
                                       </td>
                                       <td
                                          title={`% unobserved: ${unobserved.mapped_binding_residues_unobserved_percent ?? 0}%`}
                                       >
                                          {unobserved.mapped_binding_residues_unobserved}
                                       </td>
                                       <td
                                          title={`% unobserved: ${unobserved.mapped_binding_residues_unobserved_percent ?? 0}%`}
                                       >
                                          {unobserved.mapped_binding_residues_unobserved ?? '–'}
                                       </td>
                                       <td title={`aligned length: ${unobserved.aln_len}`}>{unobserved.rmsd ?? '–'}</td>
                                    </>
                                 )}
                                 <td>{unobserved.ligands ? unobserved.ligands.join(' ') : '–'}</td>
                                 <td title={`aligned length: ${unobserved.pocket_len}`}>
                                    {unobserved.pocket_rmsd ?? '–'}
                                 </td>
                                 <td title={`avg. SASA: ${unobserved.sasa_avg ?? 0}`}>{unobserved.sasa ?? '–'}</td>
                                 {exp && (
                                    <>
                                       <td title={`inverse TM-score: ${unobserved.tm_score_inv ?? 0}`}>
                                          {unobserved.tm_score ?? '–'}
                                       </td>
                                       <td>{unobserved.volume ?? '–'}</td>
                                    </>
                                 )}
                                 <td>
                                    <button
                                       className={`btn-showhide${this.context.controlsMap.get(unobserved.row_id)?.isVisible ? '' : ' hidden'}`}
                                       style={{ width: '1em' }}
                                       title="show / hide"
                                       onClick={(e): void => {
                                          e.stopPropagation();
                                          this.showHideStructure(unobserved);
                                       }}
                                    >
                                       <img src="/icons/eye.svg" alt="show/hide" width="100%" />
                                    </button>
                                 </td>
                                 <td>
                                    <button
                                       title="edit"
                                       onClick={(e): void => this.toggleEditPanel(unobserved.row_id, e)}
                                       style={{ cursor: 'pointer' }}
                                    >
                                       {this.editingRowId === unobserved.row_id ? '▼' : '▶'}
                                    </button>
                                 </td>
                                 <td>
                                    <button
                                       title={
                                          this.context.selectedPdb.some((s) => s.rowId === unobserved.row_id)
                                             ? 'Remove from Sequence Viewer'
                                             : 'Add to Sequence Viewer'
                                       }
                                       className={`btn-seq${this.context.selectedPdb.some((s) => s.rowId === unobserved.row_id) ? ' active' : ''}`}
                                       onClick={(e): void => {
                                          e.stopPropagation();
                                          this.addOrRemoveFromRcsb(unobserved);
                                       }}
                                    >
                                       {this.context.selectedPdb.some((s) => s.rowId === unobserved.row_id) ? '−' : '+'}
                                    </button>
                                 </td>
                                 <td>
                                    <button
                                       className={`btn-info${this.infoRowId === unobserved.row_id ? ' active' : ''}`}
                                       title="Show info"
                                       onClick={(e): void => this.toggleInfoPanel(unobserved.row_id, e)}
                                    >
                                       ℹ
                                    </button>
                                 </td>
                                 <td className="color-pair-cell">
                                    <ColorPairButton
                                       pocketColor={
                                          this.context.controlsMap.get(unobserved.row_id)?.pocketColor ??
                                          getDefaultPocketColor('unobserved')
                                       }
                                       chainColor={
                                          this.context.controlsMap.get(unobserved.row_id)?.chainColor ??
                                          getDefaultChainColor('unobserved')
                                       }
                                       active={this.isColorPairActive(unobserved.row_id)}
                                       onClick={(e): void => {
                                          e.stopPropagation();
                                          this.selectNextColorPair(unobserved);
                                       }}
                                       title="Apply unique color pair"
                                    />
                                 </td>
                              </tr>
                              {this.editingRowId === unobserved.row_id && (
                                 <tr>
                                    <td colSpan={colSpan} className="edit-panel-cell">
                                       <div className="edit-panel-body">
                                          <div className="edit-panel-controls">
                                             <StructureRepresentationControlComponent
                                                mode="big"
                                                values={{
                                                   chain: {
                                                      representation:
                                                         this.context.controlsMap
                                                            .get(unobserved.row_id)
                                                            ?.selectedRepresentations?.get('chain') ??
                                                         defaultRestRepresentation,
                                                      color:
                                                         this.context.controlsMap.get(unobserved.row_id)?.chainColor ??
                                                         getDefaultChainColor('unobserved'),
                                                   },
                                                   pocket: {
                                                      representation:
                                                         this.context.controlsMap
                                                            .get(unobserved.row_id)
                                                            ?.selectedRepresentations?.get('pocket') ??
                                                         defaultPocketRepresentation,
                                                      color:
                                                         this.context.controlsMap.get(unobserved.row_id)?.pocketColor ??
                                                         getDefaultPocketColor('unobserved'),
                                                   },
                                                }}
                                                onRepresentationChange={(
                                                   target: StructureComponentType,
                                                   type: Representation,
                                                ): void => this.changeRepresentation(type, unobserved, target)}
                                                onColorChange={(target: StructureComponentType, color: string): void =>
                                                   this.changeColor(unobserved, color, target)
                                                }
                                             />
                                          </div>
                                       </div>
                                    </td>
                                 </tr>
                              )}
                              {this.infoRowId === unobserved.row_id && (
                                 <tr>
                                    <td colSpan={colSpan} className="info-panel-cell">
                                       <div className="info-panel-body">
                                          <div className="info-panel-grid">
                                             <div className="info-panel-item">
                                                <span
                                                   className="info-panel-label"
                                                   data-tooltip="Resolution of the PDB structure (the best resolution is stated in the presence of multiple)."
                                                >
                                                   Res
                                                </span>
                                                <span
                                                   className="info-panel-value"
                                                   title={`Exp. method: ${unobserved.experimental_method}`}
                                                >
                                                   {unobserved.resolution ?? '–'}
                                                </span>
                                             </div>
                                             <div className="info-panel-item">
                                                <span
                                                   className="info-panel-label"
                                                   data-tooltip="Mapped binding residues (observed). Number of mapped binding residues in UniProt and SEQRES that are observed in the structure."
                                                >
                                                   MBR
                                                </span>
                                                <span
                                                   className="info-panel-value"
                                                   title={`MBR-SEQRES: ${unobserved.mapped_binding_residues_num ?? 0} (${unobserved.mapped_binding_residues_percent ?? 0}%)`}
                                                >
                                                   {unobserved.mapped_binding_residues_observed ?? '–'}
                                                </span>
                                             </div>
                                             <div className="info-panel-item">
                                                <span
                                                   className="info-panel-label"
                                                   data-tooltip="Disorder (unobserved residues). Number of mapped binding residues in UniProt and SEQRES that are not observed in the structure."
                                                >
                                                   Dis
                                                </span>
                                                <span
                                                   className="info-panel-value"
                                                   title={`% unobserved: ${unobserved.mapped_binding_residues_unobserved_percent ?? 0}%`}
                                                >
                                                   {unobserved.mapped_binding_residues_unobserved ?? '–'}
                                                </span>
                                             </div>
                                             <div className="info-panel-item">
                                                <span
                                                   className="info-panel-label"
                                                   data-tooltip="RMSD from the structural alignment (MM-align) of relevant chains (Ca atoms)."
                                                >
                                                   RMSD
                                                </span>
                                                <span
                                                   className="info-panel-value"
                                                   title={`aligned length: ${unobserved.aln_len}`}
                                                >
                                                   {unobserved.rmsd ?? '–'}
                                                </span>
                                             </div>
                                             <div className="info-panel-item">
                                                <span
                                                   className="info-panel-label"
                                                   data-tooltip="TM-score 1 from the structural alignment (MM-align) of the relevant candidate chains onto the relevant query chains."
                                                >
                                                   TM
                                                </span>
                                                <span
                                                   className="info-panel-value"
                                                   title={`inverse TM-score: ${unobserved.tm_score_inv ?? 0}`}
                                                >
                                                   {unobserved.tm_score ?? '–'}
                                                </span>
                                             </div>
                                             <div className="info-panel-item">
                                                <span
                                                   className="info-panel-label"
                                                   data-tooltip="Pocket volume calculated by PyVOL (default settings)."
                                                >
                                                   Vol
                                                </span>
                                                <span className="info-panel-value">{unobserved.volume ?? '–'}</span>
                                             </div>
                                          </div>
                                       </div>
                                    </td>
                                 </tr>
                              )}
                           </React.Fragment>
                        ))}
                     </tbody>
                  </table>
               </div>
            )}
         </div>
      );
   }
}

export default UnobservedSitesTable;
